import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Archive,
  Download,
  FileText,
  Loader2,
  Lock,
  ShieldAlert,
  Trash2,
  Database,
  AlertTriangle,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Utility formatters
// ---------------------------------------------------------------------------

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { hour12: false });
  } catch {
    return iso;
  }
};

const fmtBytes = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 ** 2).toFixed(2)} MB`;
};

const fmtStatus = (s) => {
  const map = {
    completed: { txt: "Concluído", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
    completed_with_warnings: { txt: "Concluído c/ avisos", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
    running: { txt: "Gerando…", cls: "bg-blue-500/10 text-blue-700 border-blue-500/30" },
    failed: { txt: "Falhou", cls: "bg-red-500/10 text-red-700 border-red-500/30" },
    deleted: { txt: "Excluído", cls: "bg-neutral-500/10 text-neutral-600 border-neutral-500/30" },
  };
  const v = map[s] || { txt: s || "—", cls: "bg-neutral-500/10 text-neutral-600 border-neutral-500/30" };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${v.cls}`}>{v.txt}</span>;
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function DataSnapshots() {
  const { user, apiCall } = useAuth();
  const isMaster = user?.role === "master";

  const [units, setUnits] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Form
  const [scope, setScope] = useState(isMaster ? "global" : "unit");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [includePii, setIncludePii] = useState(false); // computed default set on scope change
  const [includeBackups, setIncludeBackups] = useState(false);

  // Manifest dialog
  const [manifestFor, setManifestFor] = useState(null);
  const [manifestData, setManifestData] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);

  // Delete confirm
  const [deleteFor, setDeleteFor] = useState(null);

  // ---------- Load units + snapshots ----------
  const load = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([
        apiCall("get", "/units"),
        apiCall("get", "/foundation/snapshots"),
      ]);
      const activeUnits = (u.data || []).filter((x) => x.active !== false);
      setUnits(activeUnits);
      setSnapshots(s.data || []);
      // If non-master, pre-select their first unit
      if (!isMaster && activeUnits.length && !selectedUnit) {
        const allowed = activeUnits.find((x) => (user?.unit_ids || []).includes(x.unit_id));
        if (allowed) setSelectedUnit(allowed.unit_id);
      }
    } catch (e) {
      toast.error("Falha ao carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Update PII default when scope changes
  useEffect(() => {
    if (!isMaster) {
      setIncludePii(false);
      setIncludeBackups(false);
      return;
    }
    if (scope === "global") {
      setIncludePii(true);
    } else {
      setIncludePii(false);
      setIncludeBackups(false);
    }
  }, [scope, isMaster]);

  // ---------- Generate ----------
  const handleGenerate = async () => {
    if (scope === "unit" && !selectedUnit) {
      toast.error("Selecione uma unidade.");
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        scope,
        unit_id: scope === "unit" ? selectedUnit : null,
        include_pii: isMaster ? includePii : false,
        include_backups: isMaster && scope === "global" ? includeBackups : false,
      };
      const res = await apiCall("post", "/foundation/snapshots", payload);
      const s = res.data;
      const label = s.status === "completed" ? "Snapshot gerado com sucesso" :
                    s.status === "completed_with_warnings" ? "Snapshot gerado com avisos" :
                    "Snapshot gerado";
      toast.success(`${label} · ${fmtBytes(s.zip_size_bytes)}`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha ao gerar snapshot.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  // ---------- Download ----------
  const handleDownload = async (snap) => {
    try {
      const res = await apiCall("get", `/foundation/snapshots/${snap.snapshot_id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = snap.zip_filename || `snapshot_${snap.snapshot_id}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // refresh download counter
      load();
    } catch (e) {
      toast.error("Falha ao baixar snapshot.");
    }
  };

  // ---------- Manifest ----------
  const openManifest = async (snap) => {
    setManifestFor(snap);
    setManifestData(null);
    setManifestLoading(true);
    try {
      const res = await apiCall("get", `/foundation/snapshots/${snap.snapshot_id}/manifest`);
      setManifestData(res.data);
    } catch (e) {
      toast.error("Falha ao carregar manifest.");
      setManifestFor(null);
    } finally {
      setManifestLoading(false);
    }
  };

  // ---------- Delete ----------
  const confirmDelete = async () => {
    if (!deleteFor) return;
    try {
      await apiCall("delete", `/foundation/snapshots/${deleteFor.snapshot_id}`);
      toast.success("Snapshot excluído.");
      setDeleteFor(null);
      load();
    } catch (e) {
      toast.error("Falha ao excluir.");
    }
  };

  // ---------- Aggregate warnings ----------
  const orphanTotals = useMemo(() => {
    return snapshots.reduce((acc, s) => acc + (s.warnings?.length || 0), 0);
  }, [snapshots]);

  const availableUnitsForForm = isMaster
    ? units
    : units.filter((u) => (user?.unit_ids || []).includes(u.unit_id));

  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="data-snapshots-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Database size={14} />
            <span>Foundation</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Data Snapshots</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">
            Exportação oficial, versionada e reproduzível dos dados operacionais. Somente leitura. Nada
            neste banco é alterado durante a geração.
          </p>
        </div>
        {!isMaster && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-full">
            <Lock size={12} /> Acesso limitado à sua unidade
          </span>
        )}
      </div>

      {/* Generate card */}
      <Card data-testid="snapshot-generator-card">
        <CardHeader>
          <CardTitle className="text-lg">Gerar novo snapshot</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Escopo</Label>
              <Select value={scope} onValueChange={setScope} disabled={generating}>
                <SelectTrigger data-testid="snapshot-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isMaster && <SelectItem value="global">Global (todas as unidades)</SelectItem>}
                  <SelectItem value="unit">Por unidade</SelectItem>
                </SelectContent>
              </Select>
              {scope === "global" && (
                <p className="text-xs text-neutral-500 mt-1">
                  Inclui dados de todas as unidades, referências globais e classificação técnica de resolução por linha.
                </p>
              )}
            </div>
            <div>
              <Label>Unidade</Label>
              <Select
                value={selectedUnit}
                onValueChange={setSelectedUnit}
                disabled={scope !== "unit" || generating}
              >
                <SelectTrigger data-testid="snapshot-unit-select">
                  <SelectValue placeholder="Selecione uma unidade…" />
                </SelectTrigger>
                <SelectContent>
                  {availableUnitsForForm.map((u) => (
                    <SelectItem key={u.unit_id} value={u.unit_id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isMaster && (
            <div className="flex flex-col gap-2 pt-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
                <input
                  type="checkbox"
                  checked={includePii}
                  onChange={(e) => setIncludePii(e.target.checked)}
                  disabled={generating}
                  data-testid="snapshot-pii-checkbox"
                />
                <span>
                  Incluir dados pessoais completos (PII)
                  <span className="ml-2 text-xs text-neutral-500">
                    — telefones, e-mails, notas, aniversário. Segredos nunca são exportados.
                  </span>
                </span>
              </label>
              {scope === "global" && (
                <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
                  <input
                    type="checkbox"
                    checked={includeBackups}
                    onChange={(e) => setIncludeBackups(e.target.checked)}
                    disabled={generating}
                    data-testid="snapshot-backups-checkbox"
                  />
                  <span>
                    Incluir backups históricos (client_document_backups, appointment_backups)
                  </span>
                </label>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <ShieldAlert size={14} />
              Operação somente-leitura. Senhas, tokens e credenciais são <b>removidos</b>, não mascarados.
            </div>
            <Button
              onClick={handleGenerate}
              disabled={generating || (scope === "unit" && !selectedUnit)}
              data-testid="snapshot-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> Gerando…
                </>
              ) : (
                <>
                  <Archive size={16} className="mr-2" /> Gerar snapshot
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card data-testid="snapshots-history-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Histórico de snapshots</CardTitle>
          <span className="text-xs text-neutral-500">{snapshots.length} snapshot(s)</span>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-neutral-500">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" />
              Carregando…
            </div>
          ) : snapshots.length === 0 ? (
            <div className="p-8 text-center text-neutral-500">Nenhum snapshot ainda. Gere o primeiro acima.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-neutral-50/50">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-medium">Data</th>
                    <th className="px-4 py-2 font-medium">Escopo</th>
                    <th className="px-4 py-2 font-medium">Registros</th>
                    <th className="px-4 py-2 font-medium">Tamanho</th>
                    <th className="px-4 py-2 font-medium">PII</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Downloads</th>
                    <th className="px-4 py-2 font-medium text-right">Ações</th>
                  </tr>
                </thead>
                <tbody data-testid="snapshots-table-body">
                  {snapshots.map((s) => (
                    <tr key={s.snapshot_id} className="border-b hover:bg-neutral-50/40" data-testid={`snapshot-row-${s.snapshot_id}`}>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(s.started_at)}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {s.scope === "global" ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <Database size={12} /> Global
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs">
                            Unidade:&nbsp;
                            <b>
                              {units.find((u) => u.unit_id === s.unit_id)?.name || s.unit_id}
                            </b>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {(s.total_records ?? 0).toLocaleString("pt-BR")}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtBytes(s.zip_size_bytes)}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs">
                        {s.include_pii ? (
                          <span className="text-amber-700">Completa</span>
                        ) : (
                          <span className="text-neutral-500">Mascarada</span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtStatus(s.status)}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-neutral-600">
                        {s.download_count ?? 0}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openManifest(s)}
                            data-testid={`snapshot-manifest-btn-${s.snapshot_id}`}
                            title="Ver manifest.json"
                          >
                            <FileText size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(s)}
                            disabled={!["completed", "completed_with_warnings"].includes(s.status)}
                            data-testid={`snapshot-download-btn-${s.snapshot_id}`}
                            title="Baixar ZIP"
                          >
                            <Download size={16} />
                          </Button>
                          {isMaster && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteFor(s)}
                              data-testid={`snapshot-delete-btn-${s.snapshot_id}`}
                              title="Excluir"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manifest dialog */}
      <Dialog open={!!manifestFor} onOpenChange={(o) => !o && setManifestFor(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Manifest do snapshot</DialogTitle>
            <DialogDescription className="text-xs">
              {manifestFor?.snapshot_id} · {fmtDate(manifestFor?.started_at)}
            </DialogDescription>
          </DialogHeader>
          {manifestLoading ? (
            <div className="p-8 text-center">
              <Loader2 size={20} className="animate-spin mx-auto" />
            </div>
          ) : manifestData ? (
            <div className="overflow-auto flex-1">
              <div className="grid grid-cols-2 gap-4 text-xs mb-4">
                <div><b>Escopo:</b> {manifestData.scope}</div>
                <div><b>Ambiente:</b> {manifestData.environment}</div>
                <div><b>Cutoff:</b> {fmtDate(manifestData.snapshot_cutoff_at)}</div>
                <div><b>Duração:</b> {manifestData.duration_seconds}s</div>
                <div><b>PII:</b> {manifestData.pii_mode}</div>
                <div><b>Backups:</b> {manifestData.include_backups ? "sim" : "não"}</div>
              </div>
              {manifestData.removed_sensitive_fields?.length > 0 && (
                <div className="mb-4 text-xs">
                  <div className="font-semibold mb-1">Campos sensíveis removidos:</div>
                  <div className="flex flex-wrap gap-1">
                    {manifestData.removed_sensitive_fields.map((f) => (
                      <span key={f} className="px-1.5 py-0.5 bg-red-500/10 text-red-700 border border-red-500/30 rounded text-[10px] font-mono">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs font-semibold mb-2">Coleções ({manifestData.collections?.length}):</div>
              <table className="w-full text-xs">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="text-left px-2 py-1">Coleção</th>
                    <th className="text-right px-2 py-1">Registros</th>
                    <th className="text-left px-2 py-1">Estratégia</th>
                    <th className="text-left px-2 py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(manifestData.collections || []).map((c) => (
                    <tr key={c.collection} className="border-b border-neutral-100">
                      <td className="px-2 py-1 font-mono text-[11px]">{c.collection}</td>
                      <td className="px-2 py-1 text-right">{(c.count || 0).toLocaleString("pt-BR")}</td>
                      <td className="px-2 py-1 text-[10px] text-neutral-600">{(c.strategy || []).join(", ")}</td>
                      <td className="px-2 py-1 text-[10px]">{c.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setManifestFor(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              Excluir snapshot?
            </DialogTitle>
            <DialogDescription>
              Esta ação removerá o arquivo ZIP e o manifest do servidor. Registro permanece no audit log. Não é
              possível desfazer.
            </DialogDescription>
          </DialogHeader>
          {deleteFor && (
            <div className="text-xs bg-neutral-50 p-3 rounded border">
              <div><b>ID:</b> {deleteFor.snapshot_id}</div>
              <div><b>Data:</b> {fmtDate(deleteFor.started_at)}</div>
              <div><b>Tamanho:</b> {fmtBytes(deleteFor.zip_size_bytes)}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteFor(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="snapshot-delete-confirm-btn">
              Excluir permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
